import {
  Action, Form, FormHandlers, Message, Portal, Spacing, useModal, useNotifications,
} from '@doveit/bricks';
import React from 'react';
import validationSchema from './ArchiveJobApplicationAction.schema';
import { JobApplication } from '../../../providers/api/dtos';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import { updateJobApplication } from '../../../providers/api/job-application/jobApplicationProvider';
import { JobApplicationStatus } from '../../../domain/types';
import { koReasonOptions } from './koReasonOptions';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { useCurrentAgentSync } from '../../../hooks/use-agent/useAgent';

export interface ArchiveJobApplicationActionProps {
  jobApplication: JobApplication,
  disabled?: boolean,
  onSuccess?: (jobApplication: JobApplication) => void,
  children?: ({ openArchiveModal, disabled }: { openArchiveModal: VoidFunction, disabled?: boolean }) => React.ReactNode,
}

export const ARCHIVE_JOB_APPLICATION_SUCCESS_MESSAGE = 'La candidatura è stata archiviata con successo';
export const ARCHIVE_JOB_APPLICATION_ERROR_MESSAGE = 'Non è stato possibile archiviare la candidatura';
export const ARCHIVE_JOB_APPLICATION_INFO_MESSAGE = 'Potrai ripristinare la candidatura in qualsiasi momento.';

const DEFAULT_INITIAL_VALUES = { status: '', archivingReason: '' };

const ArchiveJobApplicationAction: React.FC<ArchiveJobApplicationActionProps> = ({
  jobApplication,
  disabled,
  onSuccess,
  children,
}) => {
  const {
    user, userIsAdmin, userIsHR, userIsAgent, mainUserRole,
  } = useRBAC();
  const { data: currentAgent } = useCurrentAgentSync();
  const isAssignedAgent = React.useMemo(() => userIsAgent && currentAgent?.email.work === jobApplication.assigneeEmail, [currentAgent?.email.work, jobApplication.assigneeEmail, userIsAgent]);
  const archiveModal = useModal();
  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;

  const [isSaving, setSaving] = React.useState(false);
  const { addSuccess, addError } = useNotifications();
  const archiveOptions = React.useMemo(() => koReasonOptions(userIsAdmin || userIsHR, isAssignedAgent, jobApplication.status), [userIsAdmin, userIsHR, isAssignedAgent, jobApplication.status]);

  const openArchiveModal = React.useCallback(
    () => archiveModal.open(),
    [archiveModal],
  );

  const submitForm = React.useCallback(() => {
    formRef.current?.handleSubmit();
  }, []);

  const onSubmit = React.useCallback(async (values: { status: string, archivingReason: string }) => {
    setSaving(true);

    const archivingReasonNormalized = values.archivingReason.trim();
    const updatedNotes = archivingReasonNormalized.length > 0
      ? [...jobApplication.notes, {
        author: user.name,
        role: mainUserRole,
        text: archivingReasonNormalized,
        date: new Date().toISOString(),
      }]
      : jobApplication.notes;

    try {
      const updatedJobApplication = await updateJobApplication(jobApplication.id!, {
        ...jobApplication,
        status: values.status as JobApplicationStatus,
        notes: updatedNotes,
      });

      addSuccess(ARCHIVE_JOB_APPLICATION_SUCCESS_MESSAGE);
      archiveModal.close();

      if (onSuccess) {
        onSuccess(updatedJobApplication);
      }
    } catch (error) {
      addError(ARCHIVE_JOB_APPLICATION_ERROR_MESSAGE);
    }

    setSaving(false);
  }, [jobApplication, user, mainUserRole, addSuccess, archiveModal, onSuccess, addError]);

  if (archiveOptions.length === 0) {
    return null;
  }

  return (
    <>
      {children
        ? children({ openArchiveModal, disabled })
        : (
          <Action
            label="Archivia"
            aria-label="Archivia candidatura"
            size="S"
            disabled={disabled}
            onClick={openArchiveModal}
          />
        )}
      <Portal>
        <SimpleModal
          {...archiveModal}
          aria-label="archive-modal"
          title="Archivia candidatura"
          footer={(
            <Action
              label="Archivia"
              color="primary"
              emphasis="high"
              aria-label="Azione per confermare l'archiviazione della candidatura"
              onClick={submitForm}
            />
          )}
        >
          <Spacing margin={[0, 0, 200]}>
            <Message
              type="info"
              message={ARCHIVE_JOB_APPLICATION_INFO_MESSAGE}
            />
          </Spacing>
          <Form
            initialValues={DEFAULT_INITIAL_VALUES}
            loading={isSaving}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            innerRef={formRef}
          >
            <Form.Item>
              <Form.Select
                name="status"
                aria-label="Campo per selezionare il motivo dell'archiviazione"
                options={archiveOptions}
              />
            </Form.Item>
            <Form.Item>
              <Form.TextArea
                rows={7}
                name="archivingReason"
                label="Note"
                aria-label="Campo per inserire le note dell'archiviazione"
                placeholder="Inserisci le note dell'archiviazione"
                required
              />
            </Form.Item>
          </Form>
        </SimpleModal>
      </Portal>
    </>
  );
};

export default ArchiveJobApplicationAction;
